// ---------------------------------------------------------------------------
// useWorkspaces — read the shared workspace list and the active selection.
//
// All state lives in `WorkspaceProvider`. This hook orders workspaces with
// the selection first, then alphabetically by displayed name. It derives
// `activeWorkspace` and reconciles selections when the fetched list changes.
// ---------------------------------------------------------------------------

import { useEffect, useMemo, useRef } from 'react';

import { useWorkspaceContext } from '../../context/WorkspaceContext.js';

import type { UseWorkspacesResult } from './useWorkspaces.interface.js';

export function useWorkspaces(): UseWorkspacesResult {
  const { activeName, setActiveName, workspaces, isLoading, error, refresh } =
    useWorkspaceContext();

  // Track activeName via a ref so the reconciliation effect can read the
  // latest value without depending on it. Including activeName in the deps
  // would re-run the effect every time the user clicks a different
  // workspace and — under any timing race against an in-flight or stale
  // workspaces array — clobber the just-made selection.
  const activeNameRef = useRef(activeName);
  activeNameRef.current = activeName;

  // Reconcile only when the *list* transitions — load/refresh — drop a
  // stored active name that no longer points to a real workspace.
  // Selecting from the dropdown must not run this.
  useEffect(() => {
    if (isLoading) return;
    if (workspaces.length === 0) return;
    const current = activeNameRef.current;
    if (!current) return;
    if (!workspaces.some((w) => w.name === current)) {
      setActiveName(null);
    }
  }, [workspaces, isLoading, setActiveName]);

  const activeWorkspace = useMemo(
    () => workspaces.find((w) => w.name === activeName) ?? null,
    [workspaces, activeName],
  );

  const orderedWorkspaces = useMemo(
    () =>
      [...workspaces].sort(
        (a, b) =>
          Number(b.name === activeName) - Number(a.name === activeName) ||
          (a.displayName ?? a.name).localeCompare(
            b.displayName ?? b.name,
            'en-US',
            { sensitivity: 'base' },
          ),
      ),
    [workspaces, activeName],
  );

  return {
    workspaces: orderedWorkspaces,
    activeWorkspace,
    isLoading,
    error,
    refresh,
  };
}
